import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

import { cn } from '@/hen-ui/utils';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role='navigation'
    aria-label='pagination'
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-1', className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  asChild?: boolean;
} & React.ComponentProps<'a'>;

const paginationLinkClassName = (isActive?: boolean) =>
  cn(
    'inline-flex h-9 min-w-9 items-center justify-center rounded-md px-3',
    'text-sm font-medium no-underline transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
    isActive
      ? 'border border-neutral-200 bg-white text-neutral-900'
      : [
          'border border-transparent bg-transparent text-neutral-900',
          'hover:!bg-neutral-100 hover:!text-neutral-900 hover:no-underline',
          'active:!bg-neutral-200 active:!text-neutral-900',
        ].join(' '),
  );

const PaginationLink = ({
  className,
  isActive,
  asChild = false,
  ...props
}: PaginationLinkProps) => {
  const Comp = asChild ? Slot : 'a';
  return (
    <Comp
      aria-current={isActive ? 'page' : undefined}
      className={cn(paginationLinkClassName(isActive), className)}
      {...props}
    />
  );
};
PaginationLink.displayName = 'PaginationLink';

type PaginationDirectionProps = React.ComponentProps<typeof PaginationLink> & {
  /** Visible word beside the chevron. An empty string leaves the chevron only. */
  label?: string;
};

const directionLabel = (label: string) => {
  const visibleLabel = label.trim();
  if (!visibleLabel) return null;

  return (
    <span className='sr-only sm:not-sr-only sm:inline'>{visibleLabel}</span>
  );
};

const PaginationPrevious = ({
  className,
  label = 'Previous',
  ...props
}: PaginationDirectionProps) => (
  <PaginationLink
    aria-label='Go to previous page'
    className={cn(
      'h-9 w-auto gap-1 px-2.5 sm:pl-2.5 text-neutral-700 no-underline',
      'hover:!bg-neutral-100 hover:!text-neutral-900 hover:no-underline',
      'active:!bg-neutral-200 active:!text-neutral-900',
      className,
    )}
    {...props}
  >
    <ChevronLeft className='h-4 w-4' aria-hidden='true' />
    {directionLabel(label)}
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  className,
  label = 'Next',
  ...props
}: PaginationDirectionProps) => (
  <PaginationLink
    aria-label='Go to next page'
    className={cn(
      'h-9 w-auto gap-1 px-2.5 sm:pr-2.5 text-neutral-900 no-underline',
      'hover:!bg-neutral-100 hover:!text-neutral-900 hover:no-underline',
      'active:!bg-neutral-200 active:!text-neutral-900',
      className,
    )}
    {...props}
  >
    {directionLabel(label)}
    <ChevronRight className='h-4 w-4' aria-hidden='true' />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className='h-4 w-4' aria-hidden='true' />
    <span className='sr-only'>More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
